/**
 * Auth Store
 * Manages the logged-in user's session, profile, and role
 *
 * @description Zustand store for authentication state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Session, User } from '@supabase/supabase-js';

// ============================================================================
// TYPES
// ============================================================================

/** User profile shape (local definition for auth context) */
export interface UserProfile {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'teacher';
  academyId?: string;
  academyName?: string;
  color?: string;
  isAppAdmin?: boolean;
}

/** Auth error types */
export type AuthErrorType = 'network' | 'auth' | 'server' | 'profile_not_found';

/** Auth error shape */
export interface AuthError {
  type: AuthErrorType;
  message: string;
  retryCount: number;
}

/** Auth liveness state (runtime only, not persisted) */
export type AuthLiveness = 'alive' | 'failing' | 'dead';

/** Auth state shape */
export interface AuthState {
  /** Supabase Session object */
  session: Session | null;
  /** Supabase User object */
  user: User | null;
  /** User's profile data */
  profile: UserProfile | null;
  /** Whether auth has been initialized (checked session at least once) */
  isInitialized: boolean;
  /** Whether profile is being loaded from server */
  isProfileLoading: boolean;
  /** Whether profile is being refreshed in background */
  isRefreshing: boolean;
  /** Auth error info */
  authError: AuthError | null;
  /** Timestamp of last successful profile fetch */
  lastProfileFetch: number | null;
  /** Whether user just logged in (for intro animation) */
  justLoggedIn: boolean;
  /** Auth liveness — runtime only, tracks token refresh health */
  authLiveness: AuthLiveness;
}

/** Auth actions */
export interface AuthActions {
  /** Set the current session and user */
  setSession: (session: Session | null) => void;
  /** Set the user's profile */
  setProfile: (profile: UserProfile | null) => void;
  /** Set profile loading state */
  setProfileLoading: (loading: boolean) => void;
  /** Set background refresh state */
  setRefreshing: (refreshing: boolean) => void;
  /** Set auth error */
  setAuthError: (error: AuthError | null) => void;
  /** Increment retry count */
  incrementRetryCount: () => void;
  /** Clear error and reset retry count */
  clearError: () => void;
  /** Update last profile fetch timestamp */
  setLastProfileFetch: (timestamp: number) => void;
  /** Clear all auth state (sign out) */
  signOut: () => void;
  /** Set justLoggedIn flag (for intro animation) */
  setJustLoggedIn: (value: boolean) => void;
  /** Set auth liveness state */
  setAuthLiveness: (liveness: AuthLiveness) => void;
}

/** Combined store type */
export type AuthStore = AuthState & AuthActions;

// ============================================================================
// DEFAULT STATE
// ============================================================================

const DEFAULT_STATE: AuthState = {
  session: null,
  user: null,
  profile: null,
  isInitialized: false,
  isProfileLoading: false,
  isRefreshing: false,
  authError: null,
  lastProfileFetch: null,
  justLoggedIn: false,
  authLiveness: 'alive',
};

const AUTH_PERSIST_KEY = 'noten_auth_store';
const WORKSPACE_PERSIST_KEY = 'noten_workspace_store';

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * useAuthStore - Authentication State Management
 *
 * Manages the current user's authentication state including:
 * - Supabase session and user objects
 * - User profile with role information
 * - Initialization tracking
 *
 * Note: This store does NOT persist to localStorage as auth state
 * is managed by Supabase's built-in session handling.
 *
 * @example
 * ```tsx
 * function UserInfo() {
 *   const { user, profile, isInitialized } = useAuthStore();
 *
 *   if (!isInitialized) return <Spinner />;
 *   if (!user) return <LoginPrompt />;
 *
 *   return (
 *     <div>
 *       <p>Welcome, {profile?.name}</p>
 *       <p>Role: {profile?.role}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Check admin status
 * const isAdmin = useAuthStore((state) => state.profile?.role === 'admin');
 * ```
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // State
      ...DEFAULT_STATE,

      // Actions
      setSession: (session) =>
        set({
          session,
          user: session?.user ?? null,
          isInitialized: true,
        }),

      setProfile: (profile) =>
        set({ profile }),

      setProfileLoading: (loading) =>
        set({ isProfileLoading: loading }),

      setRefreshing: (refreshing) =>
        set({ isRefreshing: refreshing }),

      setAuthError: (error) =>
        set({ authError: error }),

      incrementRetryCount: () =>
        set((state) => ({
          authError: state.authError
            ? { ...state.authError, retryCount: state.authError.retryCount + 1 }
            : null,
        })),

      clearError: () =>
        set({ authError: null }),

      setLastProfileFetch: (timestamp) =>
        set({ lastProfileFetch: timestamp }),

      signOut: () => {
        try {
          // Clear all user-related storage to prevent data leakage
          localStorage.removeItem(AUTH_PERSIST_KEY);
          localStorage.removeItem(WORKSPACE_PERSIST_KEY);
          // Profile cache is now in IndexedDB — cleared via AuthProvider SIGNED_OUT handler
        } catch {}
        set({
          session: null,
          user: null,
          profile: null,
          isProfileLoading: false,
          isRefreshing: false,
          authError: null,
          lastProfileFetch: null,
          justLoggedIn: false,
          authLiveness: 'alive',
          // Keep isInitialized as true - we know we're logged out
        });
      },

      setJustLoggedIn: (value) =>
        set({ justLoggedIn: value }),

      setAuthLiveness: (liveness) =>
        set({ authLiveness: liveness }),
    }),
    {
      name: AUTH_PERSIST_KEY,
      // Only persist essential state that should survive page reload
      partialize: (state) => ({
        profile: state.profile,
        lastProfileFetch: state.lastProfileFetch,
        // Don't persist: session, user (Supabase handles), isInitialized, loading states, errors, justLoggedIn
      }),
      // Skip hydration during SSR
      skipHydration: typeof window === 'undefined',
    }
  )
);

// ============================================================================
// SELECTOR HOOKS (Convenience hooks for common patterns)
// ============================================================================

/**
 * Check if user is authenticated
 * @returns Whether user is logged in
 */
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.user !== null);

/**
 * Check if user is an admin (includes owner)
 * @returns Whether user has admin or owner role
 */
export const useIsAdmin = () =>
  useAuthStore((state) => state.profile?.role === 'admin' || state.profile?.role === 'owner');

/**
 * Check if user is an owner
 * @returns Whether user has owner role
 */
export const useIsOwner = () =>
  useAuthStore((state) => state.profile?.role === 'owner');

/**
 * Check if user is app admin (개발자/앱 관리자)
 * @returns Whether user has is_app_admin flag
 */
export const useIsAppAdmin = () =>
  useAuthStore((state) => state.profile?.isAppAdmin === true);

/**
 * Check if user is a teacher
 * @returns Whether user has teacher role
 */
export const useIsTeacher = () =>
  useAuthStore((state) => state.profile?.role === 'teacher');

/**
 * Get user's display name
 * @returns User's name from profile or email fallback
 */
export const useUserDisplayName = () =>
  useAuthStore((state) => state.profile?.name ?? state.user?.email ?? null);

/**
 * Get current user's role
 * @returns User's role or null
 */
export const useUserRole = () =>
  useAuthStore((state) => state.profile?.role ?? null);

/**
 * Get current user's academy name
 * @returns User's academy name or null
 */
export const useAcademyName = () =>
  useAuthStore((state) => state.profile?.academyName ?? null);

/**
 * Get auth error
 * @returns Auth error or null
 */
export const useAuthError = () =>
  useAuthStore((state) => state.authError);

/**
 * Check if auth has error
 * @returns Whether auth has error
 */
export const useHasAuthError = () =>
  useAuthStore((state) => state.authError !== null);

/**
 * Get auth liveness state
 * @returns Auth liveness: 'alive' | 'failing' | 'dead'
 */
export const useAuthLiveness = () =>
  useAuthStore((state) => state.authLiveness);

export default useAuthStore;
